<template>
  <v-card class="modern-share-dialog" elevation="3">
    <div class="pa-6">
      <!-- Header -->
      <div class="d-flex align-center mb-6">
        <v-icon color="primary" class="mr-2">mdi-share-variant-outline</v-icon>
        <h2 class="text-h6 font-weight-medium">Share Conversation</h2>
      </div>

      <!-- Description -->
      <p class="text-body-2 text-grey-darken-1 mb-6">
        Share this conversation with others
      </p>

      <!-- Share URL. -->
      <div class="share-url-container mb-6">
        <v-text-field
          v-model="displayShareUrl"
          readonly
          outlined
          dense
          hide-details
          class="share-input"
          :class="{ 'copied': isCopied }"
        >
          <template v-slot:append>
            <v-btn
              icon
              small
              @click="copyShareUrl"
              class="copy-button"
              :color="isCopied ? 'success' : 'grey darken-1'"
            >
              <v-icon>{{ isCopied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
            </v-btn>
          </template>
        </v-text-field>
        <v-scale-transition>
          <div v-if="isCopied" class="copied-tooltip">
            Copied!
          </div>
        </v-scale-transition>
      </div>

      <!-- Voice Speed Option -->
      <div class="voice-speed-section mb-6">
        <p class="text-body-2 font-weight-medium text-grey-darken-2 mb-3">
          Default Speed
        </p>
        <div class="d-flex align-center">
          <v-icon small class="mr-2">mdi-speedometer</v-icon>
          <v-slider
            v-model="selectedVoiceSpeed"
            min="0.5"
            max="2"
            step="0.1"
            thumb-label="always"
            dense
            class="voice-speed-slider mx-2"
          ></v-slider>
          <span class="speed-value ml-2">{{selectedVoiceSpeed}}x</span>
        </div>
      </div>

      <!-- Social Sharing Buttons -->
      <div class="social-share-section mb-6">
        <p class="text-body-2 font-weight-medium text-grey-darken-2 mb-3">
          Share on
        </p>
        <div class="social-buttons">
          <!-- LinkedIn -->
          <v-btn
            small
            icon
            color="#0077B5"
            class="share-btn"
            @click="shareOn('linkedin')"
            title="Share on LinkedIn"
          >
            <v-icon>mdi-linkedin</v-icon>
          </v-btn>

          <!-- X (Twitter) -->
          <v-btn
            small
            icon
            color="#1DA1F2"
            class="share-btn"
            @click="shareOn('twitter')"
            title="Share on X"
          >
            <v-icon>mdi-twitter</v-icon>
          </v-btn>

          <!-- WhatsApp -->
          <v-btn
            small
            icon
            color="#25D366"
            class="share-btn"
            @click="shareOn('whatsapp')"
            title="Share on WhatsApp"
          >
            <v-icon>mdi-whatsapp</v-icon>
          </v-btn>

          <!-- Facebook -->
          <v-btn
            small
            icon
            color="#1877F2"
            class="share-btn"
            @click="shareOn('facebook')"
            title="Share on Facebook"
          >
            <v-icon>mdi-facebook</v-icon>
          </v-btn>

          <!-- Email -->
          <v-btn
            small
            icon
            color="#EA4335"
            class="share-btn"
            @click="shareOn('email')"
            title="Share via Email"
          >
            <v-icon>mdi-email</v-icon>
          </v-btn>

          <!-- Reddit -->
          <v-btn
            small
            icon
            color="#FF4500"
            class="share-btn"
            @click="shareOn('reddit')"
            title="Share on Reddit"
          >
            <v-icon>mdi-reddit</v-icon>
          </v-btn>

          <!-- Blogger -->
          <v-btn
            small
            icon
            color="#FF5722"
            class="share-btn"
            @click="shareOn('blogger')"
            title="Share on Blogger"
          >
            <v-icon>mdi-blogger</v-icon>
          </v-btn>

          <!-- Tumblr -->
          <v-btn
            small
            icon
            color="#35465C"
            class="share-btn"
            @click="shareOn('tumblr')"
            title="Share on Tumblr"
          >
            <v-icon>mdi-tumblr</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Visibility Toggle -->
      <div class="visibility-section">
        <p class="text-body-2 font-weight-medium text-grey-darken-2 mb-3">
          Visibility
        </p>
        <div class="visibility-toggle">
          <v-btn-toggle
            v-model="localMessage.visibility"
            mandatory
            class="visibility-buttons"
            @change="handleVisibilityChange"
          >
            <v-btn
              value="public"
              text
              class="visibility-btn"
            >
              <v-icon left size="18">mdi-earth</v-icon>
              Public
            </v-btn>
            <v-btn
              value="private"
              text
              class="visibility-btn"
            >
              <v-icon left size="18">mdi-lock</v-icon>
              Private
            </v-btn>
          </v-btn-toggle>
          <!-- Added visibility explanation text -->
          <p class="visibility-explanation text-caption text-grey-darken-1 mt-2">
            {{ visibilityExplanation }}
          </p>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
export default {
  name: 'ShareDialog',

  props: {
    message: {
      type: Object,
      required: true
    },
    shareUrl: {
      type: String,
      required: true
    },
    title: {
      type: String,
      default: 'Check out this conversation'
    },
    voiceSettings: {
      type: Object,
      default: () => ({
        enabled: true,
        rate: 1.0,
        voice: 'af_bella',
        provider: 'browser'
      })
    }
  },

  data() {
    return {
      isCopied: false,
      localMessage: { ...this.message },
      selectedVoiceSpeed: 1.0,
      displayShareUrl: ''
    }
  },

  created() {
    // Initialize voice speed from props if available
    if (this.voiceSettings && this.voiceSettings.rate) {
      this.selectedVoiceSpeed = this.voiceSettings.rate;
    }

    // Initialize the display URL
    this.updateDisplayUrl();
  },

  computed: {
    visibilityExplanation() {
      return this.localMessage.visibility === 'public'
        ? 'Anyone with the link can access'
        : 'Only you can access'
    }
  },

  watch: {
    // Watch for changes in voice speed and update the URL
    selectedVoiceSpeed: {
      handler() {
        this.updateDisplayUrl();
      }
    },

    // Watch for changes in the original share URL
    shareUrl: {
      handler() {
        this.updateDisplayUrl();
      }
    }
  },

  methods: {
    updateDisplayUrl() {
      // Create a URL object from the original share URL
      try {
        const urlObj = new URL(this.shareUrl);

        // Add or update the voice_speed parameter
        urlObj.searchParams.set('voice_speed', this.selectedVoiceSpeed);

        // Update the display share URL
        this.displayShareUrl = urlObj.toString();
      } catch (err) {
        console.error('Error updating share URL with voice speed:', err);
        this.displayShareUrl = this.shareUrl; // Fallback to original URL
      }
    },

    async copyShareUrl() {
      try {
        // Use the URL with voice speed parameter
        await navigator.clipboard.writeText(this.displayShareUrl);
        this.isCopied = true;
        setTimeout(() => {
          this.isCopied = false;
        }, 2000);
      } catch (err) {
        console.error('Failed to copy URL:', err);
        // Fallback for older browsers
        this.fallbackCopyTextToClipboard(this.displayShareUrl);
      }
    },

    fallbackCopyTextToClipboard(text) {
      const textArea = document.createElement("textarea");
      textArea.value = text;

      // Make the textarea out of viewport
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const successful = document.execCommand('copy');
        if (successful) {
          this.isCopied = true;
          setTimeout(() => {
            this.isCopied = false;
          }, 2000);
        }
      } catch (err) {
        console.error('Fallback: Unable to copy', err);
      }

      document.body.removeChild(textArea);
    },

    handleVisibilityChange() {
      this.$emit('update-visibility', { ...this.localMessage });
    },

    shareOn(platform) {
      // Use the URL with voice speed parameter
      const encodedUrl = encodeURIComponent(this.displayShareUrl);
      const encodedTitle = encodeURIComponent(this.title);
      let shareUrl = '';

      switch(platform) {
        case 'linkedin':
          shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
          break;
        case 'twitter': // X (formerly Twitter)
          shareUrl = `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`;
          break;
        case 'whatsapp':
          shareUrl = `https://api.whatsapp.com/send?text=${encodedTitle} ${encodedUrl}`;
          break;
        case 'facebook':
          shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
          break;
        case 'email':
          shareUrl = `mailto:?subject=${encodedTitle}&body=${encodedUrl}`;
          break;
        case 'reddit':
          shareUrl = `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`;
          break;
        case 'blogger':
          shareUrl = `https://www.blogger.com/blog-this.g?u=${encodedUrl}&n=${encodedTitle}`;
          break;
        case 'tumblr':
          shareUrl = `https://www.tumblr.com/widgets/share/tool?canonicalUrl=${encodedUrl}&title=${encodedTitle}`;
          break;
      }

      // Open share URL in a new window
      if (shareUrl) {
        window.open(shareUrl, '_blank', 'width=600,height=400');
        this.$emit('shared', { platform });
      }
    }
  }
}
</script>

<style scoped>
.modern-share-dialog {
  max-width: 400px;
  border-radius: 12px !important;
}

.share-url-container {
  position: relative;
}

.share-input {
  transition: all 0.3s ease;
}

.share-input.copied >>> .v-input__slot {
  border-color: var(--v-success-base) !important;
}

.copy-button {
  transition: all 0.3s ease !important;
}

.copied-tooltip {
  position: absolute;
  top: -24px;
  right: 0;
  background: var(--v-success-base);
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.voice-speed-section {
  background: rgba(0, 0, 0, 0.03);
  border-radius: 8px;
  padding: 12px;
}

.theme--dark .voice-speed-section {
  background: rgba(255, 255, 255, 0.05);
}

.voice-speed-slider {
  max-width: 250px;
}

.speed-value {
  min-width: 40px;
  text-align: right;
  font-weight: 600;
  font-size: 0.9rem;
}

.social-share-section {
  margin-top: 16px;
}

.social-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
}

.share-btn {
  transition: transform 0.2s ease !important;
}

.share-btn:hover {
  transform: scale(1.1);
}

.visibility-toggle {
  width: 100%;
}

.visibility-buttons {
  width: 100%;
  border: 1px solid var(--v-primary-base) !important;
  border-radius: 8px;
}

.visibility-btn {
  flex: 1;
  height: 40px !important;
}

.visibility-btn.v-btn--active {
  color: var(--v-primary-base) !important;
}

.visibility-explanation {
  text-align: right;
}

.v-scale-transition-enter-active,
.v-scale-transition-leave-active {
  transition: all 0.2s ease;
}

.v-scale-transition-enter,
.v-scale-transition-leave-to {
  opacity: 0;
  transform: scale(0.9);
}
</style>
